import { useState } from 'react';
import type { Camp, AvailabilityStatus } from '../../../mocks/camps';

interface CampCardProps {
  camp: Camp;
  onOpenDetail: (camp: Camp) => void;
  voteCount?: number;
  hasVoted?: boolean;
  onVote?: (campId: number) => void;
}

const availabilityConfig: Record<AvailabilityStatus, { bg: string; text: string; dot: string; icon: string }> = {
  'Spots Available':          { bg: 'bg-emerald-50 border border-emerald-200',  text: 'text-emerald-700', dot: 'bg-emerald-500', icon: 'ri-checkbox-circle-fill' },
  'Limited Spots':            { bg: 'bg-amber-50 border border-amber-200',      text: 'text-amber-700',   dot: 'bg-amber-500',   icon: 'ri-error-warning-fill' },
  'Waitlist':                 { bg: 'bg-rose-50 border border-rose-200',         text: 'text-rose-600',    dot: 'bg-rose-400',    icon: 'ri-time-fill' },
  'Registration Opens Soon':  { bg: 'bg-sky-50 border border-sky-200',           text: 'text-sky-700',     dot: 'bg-sky-400',     icon: 'ri-calendar-event-fill' },
  'Unknown — Check with Camp':{ bg: 'bg-gray-100 border border-gray-200',        text: 'text-gray-500',    dot: 'bg-gray-400',    icon: 'ri-question-fill' },
};

export default function CampCard({ camp, onOpenDetail, voteCount = 0, hasVoted = false, onVote }: CampCardProps) {
  const [animating, setAnimating] = useState(false);
  const avail = availabilityConfig[camp.availability];

  const handleVote = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (hasVoted || !onVote) return;
    setAnimating(true);
    onVote(camp.id);
    setTimeout(() => setAnimating(false), 600);
  };

  return (
    <div
      className="bg-white rounded-3xl shadow-lg overflow-hidden border border-pink-100 hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 cursor-pointer group"
      style={{ boxShadow: '0 4px 20px rgba(233, 30, 140, 0.08)' }}
      onClick={() => onOpenDetail(camp)}
    >
      {/* Image */}
      <div className="relative h-48 bg-gradient-to-br from-pink-100 to-orange-100 overflow-hidden">
        <img
          src={camp.photo}
          alt={camp.name}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#E91E8C]/30 to-transparent"></div>

        {/* Availability Badge — top left */}
        <div className="absolute top-3 left-3">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap shadow-sm ${avail.bg} ${avail.text}`}>
            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${avail.dot}`}></span>
            {camp.availability}
          </span>
        </div>

        {/* Mama Preferred Badge — top right */}
        {camp.discount && (
          <div className="absolute top-3 right-3">
            <span className="bg-gradient-to-r from-[#E91E8C] to-[#F5C518] text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg whitespace-nowrap flex items-center gap-1.5 tracking-wide">
              <span className="text-yellow-200 text-sm">✦</span>
              <span>Mama Preferred</span>
            </span>
          </div>
        )}

        {/* Duration pill */}
        <div className="absolute bottom-3 left-3">
          <span className="bg-white text-[#3D1A6E] text-xs font-bold px-3 py-1.5 rounded-full capitalize whitespace-nowrap shadow-md">
            {camp.duration.replace('-', ' ')}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        <h3 className="text-lg font-bold text-gray-800 mb-1 leading-snug">{camp.name}</h3>

        <div className="flex items-center gap-1 mb-3">
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-map-pin-line text-[#E91E8C] text-sm"></i>
          </div>
          <span className="text-sm text-gray-500">{camp.city}</span>
        </div>

        <p className="text-sm text-gray-500 mb-4 line-clamp-2 leading-relaxed">{camp.description}</p>

        {/* Mama Approved Vote Button */}
        <div className="mb-4">
          <button
            onClick={handleVote}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold transition-all duration-300 whitespace-nowrap border-2 ${
              hasVoted
                ? 'bg-pink-50 border-[#E91E8C] text-[#E91E8C] cursor-default'
                : 'bg-white border-pink-200 text-gray-600 hover:border-[#E91E8C] hover:text-[#E91E8C] hover:bg-pink-50 cursor-pointer'
            } ${animating ? 'scale-110' : 'scale-100'}`}
          >
            <span className={`text-base transition-transform duration-300 ${animating ? 'scale-125' : ''}`}>
              {hasVoted ? '👍' : '👍'}
            </span>
            <span>Mama Approved</span>
            <span className={`ml-1 font-black text-base ${hasVoted ? 'text-[#E91E8C]' : 'text-gray-500'}`}>
              {voteCount}
            </span>
            {hasVoted && (
              <span className="ml-1 text-xs font-semibold text-[#E91E8C] opacity-80">✓ You voted</span>
            )}
          </button>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs text-gray-400 mb-0.5">Ages {camp.ageRange}</div>
            <div className="text-sm font-bold text-gray-800">{camp.priceRange}</div>
          </div>
          <div className="flex gap-2">
            <button
              className="px-4 py-2 bg-pink-50 text-[#E91E8C] text-sm font-bold rounded-xl hover:bg-pink-100 transition-colors whitespace-nowrap"
              onClick={(e) => { e.stopPropagation(); onOpenDetail(camp); }}
            >
              View Details
            </button>
            {camp.registrationLink ? (
              <a
                href={camp.registrationLink}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-gradient-to-r from-[#E91E8C] to-[#D946A6] text-white text-sm font-bold rounded-xl hover:shadow-lg hover:scale-105 transition-all whitespace-nowrap flex items-center gap-1"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-pencil-line text-sm"></i>
                </div>
                Register Now
              </a>
            ) : (
              <a
                href={camp.website}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center bg-gray-100 text-gray-600 rounded-xl hover:bg-pink-100 hover:text-[#E91E8C] transition-colors"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-external-link-line text-base"></i>
                </div>
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
